import { useEffect } from 'react'

/**
 * useContentGuard — deterrent against casual copying and inspection.
 *
 * IMPORTANT: This is NOT real security. Anyone technical can still read the
 * source via view-source:, devtools opened before load, or the network tab.
 * It only raises the friction for casual users and competitors browsing
 * normally.
 *
 * BLOCKED:
 *   - Right-click / context menu across the whole page
 *   - Dragging images off the page
 *   - Inspect & view-source keyboard shortcuts (F12, Ctrl/Cmd+Shift+I/J/C,
 *     Ctrl/Cmd+U, Ctrl/Cmd+S)
 *
 * DELIBERATELY NOT BLOCKED (these hurt real patients and cost conversions):
 *   - Text selection. Patients need to copy the phone number and address.
 *   - Ctrl/Cmd+C, +P, +F, and all navigation/accessibility keys.
 *
 * Right-click and shortcuts stay enabled inside form fields so patients can
 * still paste their email and use spellcheck while typing.
 */
export function useContentGuard() {
  useEffect(() => {
    const isTypingField = (el: EventTarget | null): boolean => {
      const node = el as HTMLElement | null
      if (!node || !node.closest) return false
      return !!node.closest('input, textarea, [contenteditable="true"]')
    }

    // Block the context menu everywhere except inside text inputs
    const onContextMenu = (e: MouseEvent) => {
      if (isTypingField(e.target)) return
      e.preventDefault()
    }

    // Block dragging images out of the page
    const onDragStart = (e: DragEvent) => {
      const node = e.target as HTMLElement | null
      if (node && node.closest('img, picture, video')) {
        e.preventDefault()
      }
    }

    // Block the devtools / view-source shortcuts
    const onKeyDown = (e: KeyboardEvent) => {
      if (isTypingField(e.target)) return

      const key = e.key.toLowerCase()
      const mod = e.ctrlKey || e.metaKey

      // F12 — devtools
      if (e.key === 'F12') {
        e.preventDefault()
        return
      }

      // Ctrl/Cmd + Shift + I / J / C — devtools & element picker
      if (mod && e.shiftKey && (key === 'i' || key === 'j' || key === 'c')) {
        e.preventDefault()
        return
      }

      // Ctrl/Cmd + U — view source · Ctrl/Cmd + S — save page
      if (mod && (key === 'u' || key === 's')) {
        e.preventDefault()
      }
    }

    document.addEventListener('contextmenu', onContextMenu)
    document.addEventListener('dragstart', onDragStart)
    document.addEventListener('keydown', onKeyDown)

    return () => {
      document.removeEventListener('contextmenu', onContextMenu)
      document.removeEventListener('dragstart', onDragStart)
      document.removeEventListener('keydown', onKeyDown)
    }
  }, [])
}
